import { useEffect, useState } from 'react';
import apiClient from '@/services/api/client';
import { MockDashboardMetrics, MockActivity } from '@/mocks';
import { useShellI18n } from '@/features/language/useI18n';

const labelMap: Record<keyof MockDashboardMetrics, string> = {
  totalUsers: 'Total Users',
  activeUsers: 'Active Users',
  revenue: 'Revenue',
  systemStatus: 'System Status',
};

export const DashboardPage = () => {
  const [metrics, setMetrics] = useState<MockDashboardMetrics | null>(null);
  const [activity, setActivity] = useState<MockActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t, formatCurrency, formatDateTime } = useShellI18n();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [metricsResponse, activityResponse] = await Promise.all([
          apiClient().get('/dashboard/metrics'),
          apiClient().get('/dashboard/activity'),
        ]);
        setMetrics(metricsResponse.data);
        setActivity(activityResponse.data);
      } catch (err) {
        setError('Failed to fetch dashboard data. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <section className="page-shell">
        <div className="page-hero">
          <h1 className="page-title">{t('Loading dashboard...')}</h1>
          <div className="shell-spinner" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="page-shell">
        <div className="feedback error">{t(error)}</div>
      </section>
    );
  }

  return (
    <section className="page-shell">
      <div className="page-hero">
        <h1 className="page-title">{t('Operational Dashboard')}</h1>
        <p className="page-subtitle">{t('Unified telemetry snapshot across portal integrations and user activity.')}</p>
      </div>

      <div className="kpi-grid">
        {metrics && (Object.entries(metrics) as [keyof MockDashboardMetrics, MockDashboardMetrics[keyof MockDashboardMetrics]][]).map(([key, value]) => (
          <article key={key} className="kpi-card">
            <span className="kpi-label">{t(labelMap[key])}</span>
            <span className="kpi-value">
              {key === 'revenue'
                ? formatCurrency(Number(value))
                : String(key === 'systemStatus' ? t(String(value)) : value)}
            </span>
          </article>
        ))}
      </div>

      <article className="panel span-12">
        <div className="panel-header">
          <h2 className="panel-title">{t('Recent Activity')}</h2>
          <span className="meta">{t('{{count}} events', { count: activity.length })}</span>
        </div>
        <ul className="timeline">
          {activity.map((item) => (
            <li key={item.id} className="timeline-item">
              <h4>{t(item.action.replace(/_/g, ' '))}</h4>
              <p>{item.details ? t(item.details) : null}</p>
              <span className="timeline-meta">
                {item.user} . {formatDateTime(item.timestamp)}
              </span>
            </li>
          ))}
          {activity.length === 0 && (
            <li className="timeline-item">{t('No activity recorded.')}</li>
          )}
        </ul>
      </article>
    </section>
  );
};

export default DashboardPage;
